import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  RadioInputDiv,
  InputDiv,
  Card,
  Result,
  MathDiv,
  ActionButtonsRow,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  RadioWrapper,
  Select,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'

const PHI_OPTIONS = [5, 6.3, 8, 10, 12.5, 16, 20, 25]

export function ConcreteShearRebarFatigue() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(30)
  const [fyk, setFyk] = useState(500)
  const [b, setB] = useState('')
  const [d, setD] = useState('')
  const [diameter, setDiameter] = useState('8')
  const [spacing, setSpacing] = useState('')
  const [branchCount, setBranchCount] = useState('2')
  const [maximumShear, setMaximumShear] = useState('')
  const [minimumShear, setMinimumShear] = useState('')
  const [thetaDeg, setThetaDeg] = useState('45')
  const [alphaDeg, setAlphaDeg] = useState('90')
  const [model, setModel] = useState('1')
  const [isThetaDisabled, setIsThetaDisabled] = useState(true)
  const [isFormValid, setIsFormValid] = useState(false)
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-shear-rebar-fatigue-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      } catch (error) {
        console.error('Erro ao salvar histórico:', error)
      }
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setB(entry.b)
      setD(entry.d)
      setDiameter(entry.diameter)
      setSpacing(entry.spacing)
      setBranchCount(entry.branchCount)
      setMaximumShear(entry.maximumShear)
      setMinimumShear(entry.minimumShear)
      setThetaDeg(entry.thetaDeg)
      setAlphaDeg(entry.alphaDeg)
      setModel(entry.model)
      setIsThetaDisabled(entry.model === '1')
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value, options = {}) => {
    const { allowZero = false, isInteger = false } = options

    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (isInteger && !Number.isInteger(numValue)) {
      return 'Valor deve ser um número inteiro'
    }

    if (allowZero ? numValue < 0 : numValue <= 0) {
      return allowZero ? 'Valor deve ser maior ou igual a 0' : 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.b = validateField('b', b)
    newErrors.d = validateField('d', d)
    newErrors.spacing = validateField('spacing', spacing)
    newErrors.branchCount = validateField('branchCount', branchCount, { isInteger: true })
    newErrors.thetaDeg = validateField('thetaDeg', thetaDeg)
    newErrors.alphaDeg = validateField('alphaDeg', alphaDeg)
    newErrors.maximumShear = validateField('maximumShear', maximumShear, { allowZero: true })
    newErrors.minimumShear = validateField('minimumShear', minimumShear, { allowZero: true })

    if (!newErrors.maximumShear && !newErrors.minimumShear && Number(maximumShear) < Number(minimumShear)) {
      newErrors.maximumShear = 'Cortante máximo deve ser maior ou igual ao cortante mínimo'
    }

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null && error !== undefined)
    setIsFormValid(!hasErrors)
  }, [fck, fyk, b, d, spacing, branchCount, thetaDeg, alphaDeg, maximumShear, minimumShear, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        fck,
        fyk,
        b,
        d,
        diameter: Number(diameter),
        spacing,
        branchCount: Number(branchCount),
        maximumShear,
        minimumShear,
        thetaDeg,
        alphaDeg,
        model,
      }

      setData(data)

      const response = await api.post('/concrete/shear-rebar-fatigue', data)

      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        b,
        d,
        diameter,
        spacing,
        branchCount,
        maximumShear,
        minimumShear,
        thetaDeg,
        alphaDeg,
        model,
        data,
        result: response.data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      b,
      d,
      diameter,
      spacing,
      branchCount,
      maximumShear,
      minimumShear,
      thetaDeg,
      alphaDeg,
      model,
      isFormValid,
      data,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concreto fadiga armadura transversal.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setB(jsonData.b)
        setD(jsonData.d)
        setDiameter(jsonData.diameter)
        setSpacing(jsonData.spacing)
        setBranchCount(jsonData.branchCount)
        setMaximumShear(jsonData.maximumShear)
        setMinimumShear(jsonData.minimumShear)
        setThetaDeg(jsonData.thetaDeg)
        setAlphaDeg(jsonData.alphaDeg)
        setModel(jsonData.model)
        setIsThetaDisabled(jsonData.model === '1')
        setIsFormValid(jsonData.isFormValid)
        setData(jsonData.data)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const hasResult = result && result !== '' && result.reinforcement

  const vc = hasResult ? result.concreteShear.contribution : 0
  const halfVc = hasResult ? roundToDecimals(0.5 * vc, 3) : 0
  const maxRawTerm = hasResult ? Number(data.maximumShear) - 0.5 * vc : 0
  const minRawTerm = hasResult ? Number(data.minimumShear) - 0.5 * vc : 0
  const denominator = hasResult ? roundToDecimals(result.reinforcement.areaPerSpacing * 0.9 * data.d, 3) : 0

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Fadiga da Armadura Transversal</span>
          </div>
          <h1>🔃 Fadiga da Armadura Transversal</h1>
          <p className="subtitle">
            Verificação da variação de tensão na armadura transversal (estribos) de seções retangulares de concreto
            armado sob solicitação repetida
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>📐 Estribos</Badge>
            <Badge>🔃 Fadiga</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta verifica a fadiga da armadura transversal de seções retangulares de concreto armado,
              calculando a variação de tensão nos estribos entre os cortantes máximo e mínimo atuantes.
            </p>
            <p>
              O cálculo reutiliza a contribuição do concreto (V<sub>c</sub>) obtida pela rota de cisalhamento e
              determina a área de aço por metro (A<sub>sw</sub>/s) a partir da bitola, espaçamento e quantidade de ramos
              informados.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Modelos I e II</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b || ''}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura útil (cm)</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d || ''}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Armadura Transversal</h3>
            <InputDiv>
              <span>Bitola da barra transversal (mm)</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={diameter}
                  onChange={(event) => setDiameter(event.target.value)}
                  disabled={isLoading}
                >
                  {PHI_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      Ø {option}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Espaçamento entre estribos (cm)</span>
              <ValidatedInput
                name="spacing"
                type="number"
                value={spacing || ''}
                onChange={(event) => setSpacing(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Quantidade de ramos</span>
              <ValidatedInput
                name="branchCount"
                type="number"
                value={branchCount}
                onChange={(event) => setBranchCount(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Cortante Máximo (kN)</span>
              <ValidatedInput
                name="maximumShear"
                type="number"
                value={maximumShear}
                onChange={(event) => setMaximumShear(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Cortante Mínimo (kN)</span>
              <ValidatedInput
                name="minimumShear"
                type="number"
                value={minimumShear}
                onChange={(event) => setMinimumShear(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <RadioInputDiv>
              <span>Modelo de Cálculo</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="model"
                    id="model1"
                    value="1"
                    checked={model === '1'}
                    onChange={(e) => {
                      setModel(e.target.value)
                      setIsThetaDisabled(true)
                      setThetaDeg('45')
                    }}
                  />
                  <span>Modelo I</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="model"
                    id="model2"
                    value="2"
                    checked={model === '2'}
                    onChange={(e) => {
                      setModel(e.target.value)
                      setIsThetaDisabled(false)
                    }}
                  />
                  <span>Modelo II</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            <InputDiv>
              <span>Inclinação da biela (º)</span>
              <ValidatedInput
                name="thetaDeg"
                type="number"
                value={thetaDeg || ''}
                onChange={(event) => setThetaDeg(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading || isThetaDisabled}
              />
            </InputDiv>
            <InputDiv>
              <span>Inclinação do estribo (º)</span>
              <ValidatedInput
                name="alphaDeg"
                type="number"
                value={alphaDeg || ''}
                onChange={(event) => setAlphaDeg(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath math={`V_c = ${roundToDecimals(result.concreteShear.contribution, 2)} \\, \\text{kN}`} />
                <BlockMath
                  math={`A_{sw}/s = ${roundToDecimals(result.reinforcement.areaPerSpacing, 3)} \\, \\text{cm}^2/\\text{m}`}
                />
                <BlockMath
                  math={`\\sigma_{sw,max} = ${roundToDecimals(result.stresses.steel.maximum, 2)} \\, \\text{MPa}`}
                />
                <BlockMath
                  math={`\\sigma_{sw,min} = ${roundToDecimals(result.stresses.steel.minimum, 2)} \\, \\text{MPa}`}
                />
                <BlockMath
                  math={`\\Delta \\sigma_{sw} = ${roundToDecimals(result.stresses.steel.variation, 2)} \\, \\text{MPa}`}
                />
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Área da armadura transversal</h3>
              <MathDiv>
                <BlockMath
                  math={`A_{ramo} = ${roundToDecimals(result.reinforcement.branchArea, 3)} \\, \\text{cm}^2 \\quad (\\phi ${data.diameter} \\, \\text{mm})`}
                />
                <BlockMath math={`A_{sw} = n_{ramos} \\cdot A_{ramo}`} />
                <BlockMath
                  math={`A_{sw} = ${data.branchCount} \\cdot ${roundToDecimals(result.reinforcement.branchArea, 3)} = ${roundToDecimals(result.reinforcement.area, 3)} \\, \\text{cm}^2`}
                />
                <BlockMath math={`\\dfrac{A_{sw}}{s} = \\dfrac{A_{sw}}{s} \\cdot 100`} />
                <BlockMath
                  math={`\\dfrac{A_{sw}}{s} = \\dfrac{${roundToDecimals(result.reinforcement.area, 3)}}{${data.spacing}} \\cdot 100 = ${roundToDecimals(result.reinforcement.areaPerSpacing, 3)} \\, \\text{cm}^2/\\text{m}`}
                />
              </MathDiv>
              <h3>
                Contribuição do concreto (V<sub>c</sub>)
              </h3>
              <MathDiv>
                <p style={{ fontSize: '90%', opacity: 0.8 }}>
                  Valor obtido a partir do cálculo de cisalhamento (Modelo {data.model}) para o cortante máximo.
                </p>
                <BlockMath math={`V_c = ${roundToDecimals(result.concreteShear.contribution, 2)} \\, \\text{kN}`} />
              </MathDiv>
              <h3>Tensão na armadura transversal</h3>
              <MathDiv>
                <BlockMath
                  math={`\\sigma_{sw} = \\dfrac{1000 \\cdot max(V - 0,5 \\cdot V_c;\\, 0)}{(A_{sw}/s) \\cdot 0,9 \\cdot d}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{sw,max} = \\dfrac{1000 \\cdot max(${data.maximumShear} - 0,5 \\cdot ${roundToDecimals(vc, 2)};\\, 0)}{${roundToDecimals(result.reinforcement.areaPerSpacing, 3)} \\cdot 0,9 \\cdot ${data.d}}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`A = V_{max} - 0,5 \\cdot V_c`} />
                  <BlockMath math={`A = ${data.maximumShear} - ${halfVc} = ${roundToDecimals(maxRawTerm, 3)}`} />
                  <BlockMath math={`B = (A_{sw}/s) \\cdot 0,9 \\cdot d = ${denominator}`} />
                  <BlockMath
                    math={`\\sigma_{sw,max} = \\dfrac{1000 \\cdot max(${roundToDecimals(maxRawTerm, 3)};\\, 0)}{${denominator}}`}
                  />
                </MobileOnlyDiv>
                <BlockMath
                  math={`\\boxed{\\sigma_{sw,max} = ${roundToDecimals(result.stresses.steel.maximum, 2)} \\, \\text{MPa}}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{sw,min} = \\dfrac{1000 \\cdot max(${data.minimumShear} - 0,5 \\cdot ${roundToDecimals(vc, 2)};\\, 0)}{${roundToDecimals(result.reinforcement.areaPerSpacing, 3)} \\cdot 0,9 \\cdot ${data.d}}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`A = V_{min} - 0,5 \\cdot V_c`} />
                  <BlockMath math={`A = ${data.minimumShear} - ${halfVc} = ${roundToDecimals(minRawTerm, 3)}`} />
                  <BlockMath math={`B = (A_{sw}/s) \\cdot 0,9 \\cdot d = ${denominator}`} />
                  <BlockMath
                    math={`\\sigma_{sw,min} = \\dfrac{1000 \\cdot max(${roundToDecimals(minRawTerm, 3)};\\, 0)}{${denominator}}`}
                  />
                </MobileOnlyDiv>
                <BlockMath
                  math={`\\boxed{\\sigma_{sw,min} = ${roundToDecimals(result.stresses.steel.minimum, 2)} \\, \\text{MPa}}`}
                />
                <BlockMath math={`\\Delta \\sigma_{sw} = \\sigma_{sw,max} - \\sigma_{sw,min}`} />
                <BlockMath
                  math={`\\Delta \\sigma_{sw} = ${roundToDecimals(result.stresses.steel.maximum, 2)} - ${roundToDecimals(result.stresses.steel.minimum, 2)} = ${roundToDecimals(result.stresses.steel.variation, 2)} \\, \\text{MPa}`}
                />
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
